import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { listDrafts, deleteDraft, type AgreementDraft } from '../services/agreementDraftService';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export default function AgreementDraftsPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<AgreementDraft[]>([]);
  const [filteredDrafts, setFilteredDrafts] = useState<AgreementDraft[]>([]);
  const [selectedHospitalName, setSelectedHospitalName] = useState('');
  const [hospitalSearch, setHospitalSearch] = useState('');
  const [hospitalDropdownOpen, setHospitalDropdownOpen] = useState(false);
  const hospitalDropdownRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (hospitalDropdownRef.current && !hospitalDropdownRef.current.contains(e.target as Node)) {
        setHospitalDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadDrafts();
  }, []);

  useEffect(() => {
    // Filter drafts based on selected hospital
    if (!selectedHospitalName) {
      setFilteredDrafts(drafts);
    } else {
      setFilteredDrafts(
        drafts.filter((draft) => draft.hospitalName === selectedHospitalName)
      );
    }
  }, [drafts, selectedHospitalName]);

  // Get unique hospital names from drafts
  const uniqueHospitals = Array.from(new Set(drafts.map(d => d.hospitalName).filter(Boolean))) as string[];

  async function loadDrafts() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listDrafts();
      setDrafts(data);
    } catch (err) {
      setError('Failed to load drafts');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete(draftId: number) {
    if (!confirm('Are you sure you want to delete this draft?')) return;

    setDeletingId(draftId);
    try {
      await deleteDraft(draftId);
      setDrafts((prev) => prev.filter((d) => d.draftId !== draftId));
    } catch (err) {
      setError('Failed to delete draft');
    } finally {
      setDeletingId(null);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  }

  function formatDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    // Add 5 hours 30 minutes for IST (UTC+5:30)
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleString('en-IN', { 
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading drafts..." />;
  }

  return (
    <div className="agreement-drafts-page" style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>Agreement Drafts</h2>
        <Button onClick={() => navigate('/agreements')}>Back to Agreements</Button>
      </div>

      {/* Hospital Filter Dropdown */}
      <div style={{ marginBottom: '1.5rem', width: '100%', maxWidth: '600px' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
          Filter by Hospital
        </label>
        <div ref={hospitalDropdownRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setHospitalDropdownOpen(!hospitalDropdownOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
              backgroundColor: '#fff',
              fontSize: '0.9rem',
              color: selectedHospitalName ? '#111827' : '#9ca3af',
            }}
          >
            <span style={{ flex: 1 }}>
              {selectedHospitalName || 'All Hospitals'}
            </span>
            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>▼</span>
          </div>
          {hospitalDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 50,
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                marginTop: '2px',
                maxHeight: '260px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ padding: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Search hospitals..."
                  value={hospitalSearch}
                  onChange={(e) => setHospitalSearch(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.4rem 0.5rem',
                    fontSize: '0.85rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  aria-label="Search hospitals"
                />
              </div>
              <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
                <div
                  onClick={() => {
                    setSelectedHospitalName('');
                    setHospitalDropdownOpen(false);
                    setHospitalSearch('');
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    backgroundColor: !selectedHospitalName ? '#eff6ff' : 'transparent',
                    borderBottom: '1px solid #f3f4f6',
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => { (e.target as HTMLDivElement).style.backgroundColor = '#f3f4f6'; }}
                  onMouseLeave={(e) => { (e.target as HTMLDivElement).style.backgroundColor = !selectedHospitalName ? '#eff6ff' : 'transparent'; }}
                >
                  All Hospitals
                </div>
                {uniqueHospitals
                  .filter((name) => name.toLowerCase().includes(hospitalSearch.toLowerCase()))
                  .map((name) => (
                    <div
                      key={name}
                      onClick={() => {
                        setSelectedHospitalName(name);
                        setHospitalDropdownOpen(false);
                        setHospitalSearch('');
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        backgroundColor: name === selectedHospitalName ? '#eff6ff' : 'transparent',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                      onMouseEnter={(e) => { (e.target as HTMLDivElement).style.backgroundColor = '#f3f4f6'; }}
                      onMouseLeave={(e) => { (e.target as HTMLDivElement).style.backgroundColor = name === selectedHospitalName ? '#eff6ff' : 'transparent'; }}
                    >
                      {name}
                    </div>
                  ))}
                {uniqueHospitals.filter((name) => name.toLowerCase().includes(hospitalSearch.toLowerCase())).length === 0 && (
                  <div style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                    No hospitals found.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {filteredDrafts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          <p>{hospitalSearch ? 'No drafts found matching your search' : 'No drafts found'}</p>
          <Button onClick={() => navigate('/agreements/create')} style={{ marginTop: '1rem' }}>
            Create New Agreement
          </Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredDrafts.map((draft) => (
            <div
              key={draft.draftId}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>
                    {draft.hospitalName || 'Unnamed Hospital'}
                  </h3>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    Created by: <span style={{ fontWeight: 500, color: '#374151' }}>
                      {draft.createdByEmail || draft.createdBy}
                    </span>
                  </p>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    Last modified: {formatDateTime(draft.modifiedAt)}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    onClick={() => navigate(`/agreements/create?draftId=${draft.draftId}`)}
                    size="sm"
                  >
                    Open Draft
                  </Button>
                  <Button
                    onClick={() => handleDelete(draft.draftId)}
                    variant="danger"
                    size="sm"
                    isLoading={deletingId === draft.draftId}
                    disabled={deletingId === draft.draftId}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Valid From:</span>{' '}
                  <span style={{ color: '#6b7280' }}>{formatDate(draft.validFrom)}</span>
                </div>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Valid Till:</span>{' '}
                  <span style={{ color: '#6b7280' }}>{formatDate(draft.validTill)}</span>
                </div>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Input Parameters:</span>{' '}
                  <span style={{ color: '#6b7280' }}>
                    {draft.inputParameterIds?.length || 0}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Calculated Parameters:</span>{' '}
                  <span style={{ color: '#6b7280' }}>
                    {draft.calculatedParameters?.length || 0}
                  </span>
                </div>
              </div>

              {draft.calculatedParameters && draft.calculatedParameters.length > 0 && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                    Calculated Parameters:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    {draft.calculatedParameters.map((cp, idx) => (
                      <li key={idx}>
                        {cp.parameterName}
                        {cp.executableFormula && (
                          <span style={{ color: '#10b981', marginLeft: '0.5rem' }}>✓ Converted</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
